import React, { useContext } from 'react';
import {
  Box, List, ListItemButton, ListItemIcon, ListItemText, Badge, Divider, useTheme,
  alpha,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import TagIcon from '@mui/icons-material/Tag';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import CreateIcon from '@mui/icons-material/Create';

import { AuthContext, MyContext } from '../context/AuthContext';
import { NotificationsContext } from '../context/NotificationsContext';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  authRequired?: boolean;
  badge?: number;
}

/**
 * SIDENAV — desktop nav rail (240px).
 *
 * Lives inside the AppShell `nav` grid area on `>= lg`. On smaller
 * viewports the AppShell CSS hides this component entirely and the
 * top-bar hamburger exposes the same routes via a temporary drawer.
 *
 * Intentionally label-first (no tooltip-only icons). We have the
 * horizontal real estate and discovery matters more than density on
 * desktop where users are scanning for an action, not memorizing a
 * sprite sheet. Active route gets a left border accent and a tinted
 * background — matches the colour language on mobile's bottom bar.
 */

interface SideNavProps {
  onNavigate?: () => void; // fires after a link click (used by drawer to close itself)
  variant?: 'rail' | 'drawer';
}

const SideNav: React.FC<SideNavProps> = ({ onNavigate, variant = 'rail' }) => {
  const theme = useTheme();
  const location = useLocation();
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const { notifications } = useContext(NotificationsContext);

  const unread = notifications.filter((n) => !n.read).length;

  const primary: NavItem[] = [
    { to: '/',             label: 'Home',          icon: <HomeIcon /> },
    { to: '/list/feeds',   label: 'Feeds',         icon: <TagIcon /> },
    { to: '/leaderboard',  label: 'Leaderboard',   icon: <EmojiEventsIcon /> },
    { to: '/notifications', label: 'Notifications', icon: <NotificationsIcon />, authRequired: true, badge: unread },
    { to: '/saved',        label: 'Saved',         icon: <BookmarkBorderIcon />, authRequired: true },
  ];

  const secondary: NavItem[] = [
    userRecord
      ? { to: `/${userRecord.username}`, label: 'Profile', icon: <PersonOutlineIcon />, authRequired: true }
      : null,
    { to: '/store',        label: 'Store',         icon: <StorefrontOutlinedIcon />, authRequired: true },
    { to: '/invite',       label: 'Invite',        icon: <CardGiftcardOutlinedIcon />, authRequired: true },
    { to: '/settings',     label: 'Settings',      icon: <SettingsOutlinedIcon />, authRequired: true },
  ].filter(Boolean) as NavItem[];

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  const renderItem = (item: NavItem) => {
    if (item.authRequired && !userRecord) return null;
    const active = isActive(item.to);
    return (
      <ListItemButton
        key={item.to}
        component={Link}
        to={item.to}
        onClick={onNavigate}
        sx={{
          borderRadius: 2,
          my: 0.25,
          mx: 1,
          py: 1,
          color: active ? 'primary.main' : 'text.primary',
          bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
          fontWeight: active ? 700 : 500,
          '&:hover': {
            bgcolor: active
              ? alpha(theme.palette.primary.main, 0.12)
              : alpha(theme.palette.text.primary, 0.04),
          },
          transition: 'background-color 120ms ease',
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
          {item.badge ? (
            <Badge badgeContent={item.badge} color="error" max={99}>
              {item.icon}
            </Badge>
          ) : item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ sx: { fontWeight: 'inherit', fontSize: '0.95rem' } }}
        />
      </ListItemButton>
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        pt: variant === 'drawer' ? 2 : 2,
        pb: 2,
        width: variant === 'drawer' ? 260 : '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box component="nav">
        {primary.map(renderItem)}
      </Box>

      {secondary.length > 0 && (
        <>
          <Divider sx={{ my: 1, mx: 2 }} />
          <Box component="nav">
            {secondary.map(renderItem)}
          </Box>
        </>
      )}

      {/* Compose CTA — rail only. Fires the global compose event so the
          existing FAB dialog opens. On mobile/drawer this would collide
          with the dedicated tab bar compose button, so it's hidden. */}
      {userRecord && variant === 'rail' && (
        <Box sx={{ mt: 'auto', px: 2, pb: 2 }}>
          <ListItemButton
            onClick={() => {
              window.dispatchEvent(new CustomEvent('wipe-mobile-compose'));
              onNavigate?.();
            }}
            sx={{
              borderRadius: 0,
              border: '2px solid currentColor',
              bgcolor: 'transparent',
              color: 'text.primary',
              py: 1.25,
              justifyContent: 'center',
              gap: 1,
              fontFamily: '"VT323", monospace',
              letterSpacing: 1,
              textTransform: 'uppercase',
              '&:hover': {
                bgcolor: 'action.hover',
                boxShadow: '2px 2px 0 0 currentColor',
                transform: 'translate(-1px, -1px)',
              },
              transition: 'all 0.08s steps(2, jump-none)',
            }}
          >
            <CreateIcon />
            <ListItemText
              primary="Post"
              primaryTypographyProps={{ sx: { fontWeight: 700, textAlign: 'center' } }}
            />
          </ListItemButton>
        </Box>
      )}
    </Box>
  );
};

export default SideNav;
